import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import { Loader2, ArrowLeft, DollarSign, TrendingUp, Radio, Users, Search, ExternalLink, AlertOctagon, Mail, CheckCircle2, AlertTriangle } from 'lucide-react';

const Stat = ({ label, value, sub, accent = false }) => (
  <div className={`rounded-md border p-5 ${accent ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'}`}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-1 text-3xl font-mono font-bold ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

// Diagnostics chip — renders a green tick when configured, amber warning otherwise.
// Booleans-only data: the actual Price IDs / secrets are never sent to the frontend.
const StripeConfigChip = ({ label, ok }) => (
  <div
    className={`flex items-center gap-2 px-3 py-2 rounded-sm border text-xs ${
      ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/10'
    }`}
    data-testid={`stripe-config-${ok ? 'ok' : 'missing'}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    {ok ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
    ) : (
      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
    )}
    <span className={`font-mono ${ok ? 'text-foreground/90' : 'text-amber-200'}`}>{label}</span>
    <span className={`ml-auto text-[10px] uppercase tracking-widest ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>
      {ok ? 'configured' : 'missing'}
    </span>
  </div>
);

const AdminBillingPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    adminApi
      .billingOverview()
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          clearAdminToken();
          navigate('/admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Failed to load billing data.
      </div>
    );
  }

  const totals = data.totals;
  const rows = (data.tenants || []).filter((t) =>
    !search || (t.tenant_name || '').toLowerCase().includes(search.toLowerCase()) || (t.client_id || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-billing-page">
      <header className="border-b border-border bg-card/40 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="admin-back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" /> Tenants
          </button>
          <h1 className="text-base font-bold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Billing & Revenue
          </h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Failed payments alert strip — only renders when any tenant is past-due */}
        {totals.tenants_with_failed_payment > 0 && (
          <div
            className="rounded-md border border-red-500/50 bg-red-500/5 p-5 flex items-start gap-4"
            data-testid="failed-payments-alert"
          >
            <AlertOctagon className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-sm uppercase tracking-widest font-bold text-red-300">Failed payments</h3>
                <span className="font-mono text-sm font-semibold text-red-200" data-testid="failed-payments-count">
                  {totals.tenants_with_failed_payment} tenant{totals.tenants_with_failed_payment === 1 ? '' : 's'}
                </span>
                <span className="font-mono text-sm font-semibold text-red-200" data-testid="failed-payments-amount">
                  {totals.failed_payment_amount_display} outstanding
                </span>
              </div>
              <p className="text-sm text-red-100/80 mt-2">
                Stripe couldn't charge these tenants on their last invoice. They've been auto-emailed
                with the hosted invoice link. Past-due tenants are pinned to the top of the table below.
              </p>
            </div>
          </div>
        )}

        {/* Headline stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat
            label="MRR (total)"
            value={totals.mrr_total_display}
            sub={`${data.currency} per month`}
            accent
            data-testid="admin-stat-mrr"
          />
          <Stat
            label="ARR (annualised)"
            value={totals.arr_total_display}
            sub="12 × current MRR"
          />
          <Stat
            label="Pro tenants"
            value={totals.tenants_pro}
            sub={`+ ${totals.tenants_free} on Free`}
          />
          <Stat
            label="GPS devices"
            value={totals.gps_devices_total}
            sub={`@ ${data.currency} $${(data.gps_price_cents / 100).toFixed(2)} ea`}
          />
        </div>

        {/* MRR breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-md border border-border bg-card p-5">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-primary" />
              <h3 className="text-sm uppercase tracking-widest font-semibold">Plan revenue</h3>
            </div>
            <p className="mt-3 text-2xl font-mono font-bold">
              {data.currency} ${(totals.mrr_pro_cents / 100).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {totals.tenants_pro} Pro tenant{totals.tenants_pro === 1 ? '' : 's'} × {data.currency} ${(data.pro_price_cents / 100).toFixed(2)} flat
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <div className="flex items-center gap-3">
              <Radio className="w-5 h-5 text-primary" />
              <h3 className="text-sm uppercase tracking-widest font-semibold">GPS Telemetry revenue</h3>
            </div>
            <p className="mt-3 text-2xl font-mono font-bold">
              {data.currency} ${(totals.mrr_gps_cents / 100).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {totals.gps_devices_total} device{totals.gps_devices_total === 1 ? '' : 's'} × {data.currency} ${(data.gps_price_cents / 100).toFixed(2)} per asset / mo
            </p>
          </div>
        </div>

        {/* Stripe configuration diagnostics — booleans only, no values leaked.
            Lets a super-admin verify the production env without SSH-ing into the VM. */}
        {data.stripe_config && (
          <div className="rounded-md border border-border bg-card p-5" data-testid="stripe-config-card">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
              <div>
                <h3 className="text-sm uppercase tracking-widest font-semibold">Stripe configuration</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Production env diagnostics. Values stay on the server — only presence is reported here.
                </p>
              </div>
              {Object.values(data.stripe_config).every(Boolean) ? (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 text-xs font-mono">
                  <CheckCircle2 className="w-3.5 h-3.5" /> All set
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs font-mono">
                  <AlertTriangle className="w-3.5 h-3.5" /> Incomplete
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <StripeConfigChip label="STRIPE_SECRET_KEY"        ok={data.stripe_config.api_key} />
              <StripeConfigChip label="STRIPE_PRO_PRICE_ID"      ok={data.stripe_config.pro_price_id} />
              <StripeConfigChip label="STRIPE_GPS_PRICE_ID"      ok={data.stripe_config.gps_price_id} />
              <StripeConfigChip label="STRIPE_USER_SEAT_PRICE_ID" ok={data.stripe_config.user_seat_price_id} />
              <StripeConfigChip label="STRIPE_WEBHOOK_SECRET"    ok={data.stripe_config.webhook_secret} />
            </div>
            {!Object.values(data.stripe_config).every(Boolean) && (
              <p className="text-xs text-muted-foreground mt-3">
                Missing values come from GitHub Actions secrets injected by{' '}
                <code className="font-mono text-[11px]">.github/workflows/deploy-azure-vm.yml</code>.
                Add the missing secret in GitHub → Settings → Secrets and Variables → Actions, then redeploy.
              </p>
            )}
          </div>
        )}

        {/* Per-tenant breakdown */}
        <div>
          <div className="flex items-center justify-between gap-4 mb-3">
            <h2 className="text-lg font-bold uppercase tracking-tight">Tenants by MRR</h2>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name or client ID…"
                className="w-full h-9 pl-9 pr-3 rounded-md bg-card border border-border text-sm"
                data-testid="admin-billing-search"
              />
            </div>
          </div>

          <div className="rounded-md border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Tenant</th>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-right p-3">Assets</th>
                  <th className="text-right p-3">GPS</th>
                  <th className="text-right p-3">MRR</th>
                  <th className="text-left p-3">Stripe</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr
                    key={t.tenant_id}
                    className={`border-t border-border ${t.failed_payment ? 'bg-red-500/5' : ''}`}
                    data-testid={`admin-billing-row-${t.tenant_id}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{t.tenant_name}</span>
                        {t.failed_payment && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-bold bg-red-500/15 text-red-300 border border-red-500/40"
                            data-testid={`failed-payment-badge-${t.tenant_id}`}
                            title={`Attempt #${t.failed_payment.attempt_count} · ${t.failed_payment.amount_due_display}`}
                          >
                            <AlertOctagon className="w-3 h-3" />
                            Payment failed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{t.client_id}</div>
                      {t.failed_payment && (
                        <div className="text-[11px] text-red-200/80 mt-1 flex items-center gap-2 flex-wrap">
                          <span className="font-mono">{t.failed_payment.amount_due_display}</span>
                          <span>· attempt #{t.failed_payment.attempt_count}</span>
                          {t.failed_payment.hosted_invoice_url && (
                            <a
                              href={t.failed_payment.hosted_invoice_url}
                              target="_blank"
                              rel="noreferrer"
                              className="underline text-red-200 hover:text-red-100"
                              data-testid={`failed-invoice-link-${t.tenant_id}`}
                            >
                              View invoice
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-bold ${
                        t.is_pro ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-muted text-muted-foreground border border-border'
                      }`}>
                        {t.plan}
                      </span>
                      {t.subscription_status && (
                        <div className="text-[10px] text-muted-foreground mt-1">{t.subscription_status}</div>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono">{t.asset_count}</td>
                    <td className="p-3 text-right font-mono">{t.gps_count}</td>
                    <td className="p-3 text-right font-mono font-semibold">{t.mrr_display}</td>
                    <td className="p-3">
                      {t.stripe_customer_id ? (
                        <a
                          href={`https://dashboard.stripe.com/test/customers/${t.stripe_customer_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                          data-testid={`stripe-customer-link-${t.tenant_id}`}
                        >
                          {t.stripe_customer_id.slice(0, 14)}… <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No tenants match the filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-muted-foreground italic flex items-center gap-2">
          <TrendingUp className="w-3 h-3" /> MRR is computed locally from tenant state — not a Stripe roundtrip. Stripe Dashboard is the source of truth for live invoices &amp; payments.
        </p>
      </main>
    </div>
  );
};

export default AdminBillingPage;
